import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  clearError as clearRoleError,
} from 'store/slices/roleSlice';
import {
  fetchPermissions,
  fetchResourceOptions,
  savePermissions,
  deletePermission,
  bulkCreatePermissions,
  clearError as clearPermissionError,
  togglePermission,
  setCurrentRolePermissions,
  clearCurrentRolePermissions,
  toggleAllPermissions,
  toggleAllForResource,
} from 'store/slices/permissionSlice';
import {
  Pencil,
  Trash2,
  Shield,
  Loader2,
  MoreVertical,
  Plus,
  CheckSquare,
  Square,
  Search,
  X,
  ChevronsUpDown,
  Check,
} from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import FormField from '../../components/common/FormField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from 'components/ui/command';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from 'components/ui/popover';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../components/ui/dropdown-menu';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogContent,
} from '../../components/ui/alert-dialog';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';
import { cn } from 'lib/utils';
import { restoreRole } from 'store/slices/commonSlice';

const RolePage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();

  // // Suppress harmless ResizeObserver warning in development (common with Radix UI)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const originalError = console.error;
      console.error = (...args) => {
        if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
          return;
        }
        originalError(...args);
      };
      return () => {
        console.error = originalError;
      };
    }
  }, []);

  /* ---------- REDUX STATE ---------- */
  const { roles, pagination: rolePagination, isLoading: roleLoading, error: roleError } = useSelector((s) => s.role);
  const { currentRolePermissions, resourceOptions, isLoading: permLoading, isLoadingOptions, error: permError } = useSelector((s) => s.permission);

  /* ---------- PERMISSIONS ---------- */
  const rolePerms = usePermissions('role');
  const permPerms = usePermissions('permissions');
  const { canRead: canReadRole, canCreate: canCreateRole, canUpdate: canUpdateRole, canDelete: canDeleteRole } = rolePerms;
  const { canRead: canReadPerm, canCreate: canCreatePerm, canUpdate: canUpdatePerm, canDelete: canDeletePerm } = permPerms;

  /* ---------- LOCAL STATE ---------- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isPermDialogOpen, setIsPermDialogOpen] = useState(false);
  const [isAddPermConfirmOpen, setIsAddPermConfirmOpen] = useState(false);
  const [isRoleDeleteOpen, setIsRoleDeleteOpen] = useState(false);
  const [isPermDeleteOpen, setIsPermDeleteOpen] = useState(false);
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [permToDelete, setPermToDelete] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', level: '', is_active: true });
  const [roleErrors, setRoleErrors] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [permSearch, setPermSearch] = useState('');
  const [selectedResources, setSelectedResources] = useState([]);
  const [openMultiSelect, setOpenMultiSelect] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef(null);

  /* ---------- REFETCH ROLES ---------- */
  const refetch = useCallback(() => {
    if (canReadRole) {
      dispatch(fetchRoles({ page, pageSize, search: searchTerm }));
    }
  }, [dispatch, page, pageSize, searchTerm, canReadRole]);

  useEffect(() => refetch(), [refetch]);

  /* ---------- FETCH RESOURCE OPTIONS ---------- */
  useEffect(() => {
    if (canReadPerm) {
      dispatch(fetchResourceOptions());
    }
  }, [dispatch, canReadPerm]);

  /* ---------- GLOBAL TOASTS ---------- */
  useEffect(() => {
    if (roleError) {
      toast.error(typeof roleError === 'string' ? roleError : 'Role error');
      dispatch(clearRoleError());
    }
  }, [roleError, dispatch]);

  useEffect(() => {
    if (permError) {
      const msg = typeof permError === 'object'
        ? Object.entries(permError).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join('; ')
        : permError;
      toast.error(msg || 'Permission error');
      dispatch(clearPermissionError());
    }
  }, [permError, dispatch]);

  /* ---------- ROLE CRUD ---------- */
  const validateRole = () => {
    const e = {};
    if (!roleForm.name.trim()) e.name = 'Name required';
    if (!roleForm.level) e.level = 'Level required';
    setRoleErrors(e);
    return Object.keys(e).length === 0;
  };

  const openRoleDialog = (role = null) => {
    if (role) {
      if (!canUpdateRole) return toast.error("You don't have permission to edit role");
      setCurrentRole(role);
      setRoleForm({
        name: role.name,
        level: role.level?.replace('level_', '') || '',
        is_active: role.is_active,
      });
    } else {
      if (!canCreateRole) return toast.error("You don't have permission to create role");
      setCurrentRole(null);
      setRoleForm({ name: '', level: '', is_active: true });
    }
    setRoleErrors({});
    setIsRoleDialogOpen(true);
  };

  const submitRole = async (e) => {
    e.preventDefault();
    if (!validateRole()) return;
    const payload = {
      name: roleForm.name,
      level: `level_${roleForm.level}`,
      is_active: roleForm.is_active,
    };
    try {
      let newRole;
      if (currentRole) {
        await dispatch(updateRole({ id: currentRole.id, ...payload })).unwrap();
        toast.success('Role updated');
      } else {
        newRole = await dispatch(createRole(payload)).unwrap();
        toast.success('Role created');
      }
      refetch();
      setIsRoleDialogOpen(false);
      if (!currentRole && newRole && canReadPerm) {
        setTimeout(() => openPermissionDialog(newRole), 300);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const confirmDeleteRole = async () => {
    if (!canDeleteRole) return toast.error("You don't have permission to delete role");
    try {
      await dispatch(deleteRole(currentRole.id)).unwrap();
      toast.success('Role deleted');
      refetch();
      setIsRoleDeleteOpen(false);
    } catch { }
  };

  /* ---------- PERMISSION MANAGEMENT ---------- */
  const openPermissionDialog = async (role) => {
    if (!canReadPerm) return toast.error("You don't have permission to view permissions");
    setCurrentRole(role);
    setSelectedResources([]);
    setPermSearch('');
    setIsPermDialogOpen(true);
    try {
      const res = await dispatch(fetchPermissions({ roleId: role.id })).unwrap();
      dispatch(setCurrentRolePermissions(res));
      setOriginalPermissions(JSON.parse(JSON.stringify(res)));
    } catch (err) {
      console.error(err);
    }
  };

  const availableResources = useMemo(() => {
    return resourceOptions.filter((o) => !currentRolePermissions.some(p => p.resource === o.value));
  }, [resourceOptions, currentRolePermissions]);

  // Select All logic
  const allAvailableSelected = availableResources.length > 0 && availableResources.every(r => selectedResources.includes(r.value));
  const someAvailableSelected = selectedResources.length > 0 && selectedResources.length < availableResources.length;

  const toggleSelectAll = () => {
    if (allAvailableSelected) {
      setSelectedResources([]);
    } else {
      setSelectedResources(availableResources.map(r => r.value));
    }
  };

  const handleResourceToggle = (resourceValue) => {
    setSelectedResources(prev => {
      if (prev.includes(resourceValue)) {
        return prev.filter(v => v !== resourceValue);
      }
      return [...prev, resourceValue];
    });
  };

  const confirmAddMultiplePermissions = () => {
    if (selectedResources.length === 0) return;
    if (!canCreatePerm) return toast.error("You don't have permission to add permissions");
    setIsAddPermConfirmOpen(true);
  };

  const performBulkCreate = async () => {
    try {
      await dispatch(bulkCreatePermissions({
        roleId: currentRole.id,
        resources: selectedResources,
      })).unwrap();

      toast.success(`Added ${selectedResources.length} new permission${selectedResources.length > 1 ? 's' : ''}`);

      const fresh = await dispatch(fetchPermissions({ roleId: currentRole.id })).unwrap();
      dispatch(setCurrentRolePermissions(fresh));
      setOriginalPermissions(JSON.parse(JSON.stringify(fresh)));

      setSelectedResources([]);
      setIsAddPermConfirmOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to add permissions';
      toast.error(msg);
    }
  };

  const requestDeletePermission = (perm) => {
    if (!canDeletePerm) return toast.error("You don't have permission to delete permission");
    setPermToDelete(perm);
    setIsPermDeleteOpen(true);
  };

  const confirmDeletePermission = async () => {
    try {
      await dispatch(deletePermission(permToDelete.id)).unwrap();
      const fresh = await dispatch(fetchPermissions({ roleId: currentRole.id })).unwrap();
      dispatch(setCurrentRolePermissions(fresh));
      setOriginalPermissions(JSON.parse(JSON.stringify(fresh)));
      toast.success('Permission removed');
      setIsPermDeleteOpen(false);
      setPermToDelete(null);
    } catch { }
  };

  /* ---------- SAVE & TOGGLES ---------- */
  const hasEmptyPermissions = useMemo(() => {
    return currentRolePermissions.some(p =>
      !p.can_create && !p.can_read && !p.can_update && !p.can_delete && !p.can_import && !p.can_export
    );
  }, [currentRolePermissions]);

  const hasResourceWithoutRead = useMemo(() => {
    return currentRolePermissions.some(p => !p.can_read);
  }, [currentRolePermissions]);

  const saveAllPermissions = async () => {
    if (!canUpdatePerm) return toast.error("You don't have permission to save permissions");
    if (hasResourceWithoutRead) {
      toast.error("Cannot save: Read permissions are required for all resources.");
      return;
    }
    if (hasEmptyPermissions) {
      setIsSaveConfirmOpen(true);
      return;
    }
    await performSave();
  };

  const performSave = async () => {
    try {
      await dispatch(savePermissions({
        roleId: currentRole.id,
        permissions: currentRolePermissions,
        originalPermissions,
      })).unwrap();
      toast.success('Permissions saved');
      setIsPermDialogOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to save');
    }
  };

  const allChecked = currentRolePermissions.length > 0 && currentRolePermissions.every(p =>
    ['can_create', 'can_read', 'can_update', 'can_delete', 'can_import', 'can_export'].every(f => p[f])
  );

  const toggleAll = () => {
    if (!canUpdatePerm) return;
    dispatch(toggleAllPermissions(!allChecked));
  };

  // const toggleAll = () => {
  //   if (!canUpdatePerm) return;

  //   // Always enable all permissions when "Check All" is clicked
  //   dispatch(toggleAllPermissions(true));
  // };
  // const toggleResourceAll = (permId) => {
  //   if (!canUpdatePerm) return;
  //   const current = currentRolePermissions.find(p => p.id === permId);
  //   const enabled = !current?.can_create;
  //   dispatch(toggleAllForResource({ permId, enabled }));
  // };

  const toggleResourceAll = (permId) => {
    if (!canUpdatePerm) return;
    const current = currentRolePermissions.find(p => p.id === permId);
    const allCurrentlyChecked = ['can_create', 'can_read', 'can_update', 'can_delete', 'can_import', 'can_export']
      .filter(f => f !== 'can_read' || current.can_read) // preserve read if already set
      .every(f => current[f]);

    // Toggle opposite of current state
    dispatch(toggleAllForResource({ permId, enabled: !allCurrentlyChecked }));
  };
  const filteredPermissions = useMemo(() => {
    if (!permSearch.trim()) return currentRolePermissions;
    const query = permSearch.toLowerCase();
    return currentRolePermissions.filter(p =>
      (p.resource_display || p.resource).toLowerCase().includes(query)
    );
  }, [currentRolePermissions, permSearch]);

  /* ---------- TABLE COLUMNS ---------- */
  const columns = [
    { accessorKey: 'name', header: 'Role Name', cell: ({ row }) => <span className="font-semibold text-foreground">{row.original.name}</span> },
    {
      accessorKey: 'is_active', header: 'Status', cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'success' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    { accessorKey: 'created_at', header: 'Created On', cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span> },
    { accessorKey: 'created_by_name', header: 'Created By', cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span> },
    {
      id: 'actions', header: 'Actions', cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canReadPerm && (
              <DropdownMenuItem onClick={() => openPermissionDialog(row.original)} className="flex items-center gap-2">
                <Shield className="h-4 w-4" /> Permissions
              </DropdownMenuItem>
            )}
            {canUpdateRole && (
              <DropdownMenuItem onClick={() => openRoleDialog(row.original)} className="flex items-center gap-2">
                <Pencil className="h-4 w-4" /> Edit
              </DropdownMenuItem>
            )}
            {canDeleteRole && (
              <DropdownMenuItem onClick={() => { setCurrentRole(row.original); setIsRoleDeleteOpen(true); }} className="text-destructive flex items-center gap-2">
                <Trash2 className="h-4 w-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  ];

  /* ---------- RENDER ---------- */
  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Roles"
        description="Manage roles and their granular permissions"
        action={canCreateRole ? { label: 'Add Role', onClick: () => openRoleDialog(), icon: <Plus className="h-4 w-4" /> } : null}
      />

      {/* {roleLoading && !roles.length ? (
        <div className="flex justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span className="text-muted-foreground">Loading roles...</span>
        </div>
      ) : null} */}

      {canReadRole ? (
        <DataTable
          data={roles}
          totalCount={rolePagination.count}
          columns={columns}
          searchPlaceholder="Search roles..."
          showExport={true}
          showImport={false}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={Math.ceil(rolePagination.count / rolePagination.page_size)}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={roleLoading}
          onDelete={async (id) => {
            await dispatch(deleteRole(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'roles', id }));
            refetch();
          }}
        />
      ) : (
        <p className="text-center text-muted-foreground py-8">No access to view roles</p>
      )}

      {/* ROLE FORM DIALOG */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{currentRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            <DialogDescription>
              {currentRole ? 'Update role details' : 'Define a new role with access level'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRole} className="space-y-5">
            <FormField label="Role Name" name="name" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} error={roleErrors.name} required placeholder="e.g. Manager, Viewer" />
            <div className="space-y-2">
              <Label>Access Level <span className="text-destructive">*</span></Label>
              <Select value={roleForm.level} onValueChange={(v) => setRoleForm({ ...roleForm, level: v })}>
                <SelectTrigger className={roleErrors.level ? "border-red-500" : ""}>
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((l) => (
                    <SelectItem key={l} value={l.toString()}>Level {l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {roleErrors.level && <p className="text-sm text-destructive">{roleErrors.level}</p>}
            </div>
            <FormField label="Status" name="is_active" type="switch" checked={roleForm.is_active} onCheckedChange={(c) => setRoleForm({ ...roleForm, is_active: c })} labelOn="Active" labelOff="Inactive" />
            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setIsRoleDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={roleLoading}>
                {roleLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {currentRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PERMISSIONS DIALOG */}
      <Dialog open={isPermDialogOpen} onOpenChange={(open) => {
        setIsPermDialogOpen(open);
        if (!open) {
          dispatch(clearCurrentRolePermissions());
          setCurrentRole(null);
          setSelectedResources([]);
          setPermSearch('');
        }
      }}>
        <DialogContent className="max-w-5xl p-0 gap-0 max-h-[85vh] overflow-hidden flex flex-col">
          <div className="sticky top-0 z-10 bg-background border-b px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <DialogTitle className="text-lg sm:text-xl font-semibold">
              Permissions for <span className="text-primary">{currentRole?.name}</span>
            </DialogTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {canUpdatePerm && currentRolePermissions.length > 0 && (
                <Button variant="outline" size="sm" onClick={toggleAll} className="flex items-center gap-1.5">
                  {allChecked ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  <span className="hidden sm:inline">Check All</span>
                </Button>
              )}
              {canUpdatePerm && (
                <Button onClick={saveAllPermissions} disabled={permLoading} size="sm">
                  {permLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : 'Save'}
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => setIsPermDialogOpen(false)} className="h-8 w-8 -mr-2 sm:mr-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="px-4 sm:px-6 pt-4 border-b pb-3">
            <div className="flex flex-col sm:flex-row gap-3">
              {canCreatePerm && (
                <div className="flex-1">
                  {isLoadingOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading resources...
                    </div>
                  ) : availableResources.length === 0 ? (
                    <Button variant="outline" disabled className="w-full">No resources available</Button>
                  ) : (
                    <Popover open={openMultiSelect} onOpenChange={setOpenMultiSelect}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-expanded={openMultiSelect} className="w-full justify-between font-normal">
                          {selectedResources.length === 0
                            ? "Add multiple permissions..."
                            : `${selectedResources.length} selected`}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4} >
                        <Command shouldFilter={false} className="rounded-lg border shadow-md" onWheel={(e) => e.stopPropagation()}>
                          <CommandInput placeholder="Search resources..." className="h-10" value={searchQuery} onValueChange={setSearchQuery} />
                          <CommandList className="max-h-[300px] overflow-y-auto scrollbar-thin">
                            <CommandEmpty>No resource found</CommandEmpty>
                            <CommandGroup>
                              {/* Parent "Select All" Checkbox */}
                              {availableResources.length > 0 && (
                                <CommandItem onSelect={toggleSelectAll} className="cursor-pointer">
                                  <div className="flex items-center gap-2 w-full">
                                    <div className={cn(
                                      "h-4 w-4 border rounded-sm flex items-center justify-center",
                                      allAvailableSelected ? "bg-primary border-primary" : "border-muted-foreground",
                                      someAvailableSelected && "bg-primary/30 border-primary"
                                    )}>
                                      {allAvailableSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                                      {someAvailableSelected && !allAvailableSelected && <div className="h-2 w-2 bg-primary rounded-sm" />}
                                    </div>
                                    <span className="font-medium">Select All ({availableResources.length})</span>
                                  </div>
                                </CommandItem>
                              )}

                              {/* Filtered Resources */}
                              {availableResources
                                .filter((r) => r.display_name.toLowerCase().includes(searchQuery.toLowerCase()))
                                .map((resource) => (
                                  <CommandItem
                                    key={resource.value}
                                    onSelect={() => handleResourceToggle(resource.value)}
                                    className="cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2 w-full">
                                      <div className={cn(
                                        "h-4 w-4 border rounded-sm flex items-center justify-center",
                                        selectedResources.includes(resource.value) ? "bg-primary border-primary" : "border-muted-foreground"
                                      )}>
                                        {selectedResources.includes(resource.value) && <Check className="h-3 w-3 text-primary-foreground" />}
                                      </div>
                                      <span>{resource.display_name}</span>
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}

              <div className="relative flex-1 sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search permissions..." value={permSearch} onChange={(e) => setPermSearch(e.target.value)} className="pl-10 pr-8" />
                {permSearch && (
                  <button onClick={() => setPermSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Add Button */}
            {selectedResources.length > 0 && (
              <div className="mt-3 flex items-center justify-between">
                <Badge variant="secondary">{selectedResources.length} resources selected</Badge>
                <Button onClick={confirmAddMultiplePermissions} size="sm">
                  Add {selectedResources.length} Permission{selectedResources.length > 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </div>

          {/* Permissions Cards List */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6">
            <div className="py-4">
              {permLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-muted-foreground">Loading permissions...</span>
                </div>
              ) : filteredPermissions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>{permSearch ? `No permissions found for "${permSearch}"` : 'No permissions assigned yet.'}</p>
                  {!permSearch && <p className="text-sm mt-1">Add one using the dropdown above.</p>}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPermissions.map((p) => {
                    const allCheckedForResource = ['can_create', 'can_read', 'can_update', 'can_delete', 'can_import', 'can_export'].every(f => p[f]);
                    const hasNoRead = !p.can_read;

                    return (
                      <Card key={p.id} className={cn(
                        "border shadow-sm hover:shadow-md transition-all duration-200",
                        hasNoRead && "border-red-500/70 bg-red-50/50 ring-2 ring-red-200"
                      )}>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <CardTitle className="text-base font-semibold truncate">
                              {p.resource_display || p.resource}
                            </CardTitle>
                            {hasNoRead && <Badge variant="destructive" className="text-xs">No Read Access</Badge>}
                            {canUpdatePerm && (
                              <Button variant="ghost" size="sm" onClick={() => toggleResourceAll(p.id)} className="h-8 px-2 text-xs">
                                {allCheckedForResource ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                <span className="hidden sm:inline ml-1">All</span>
                              </Button>
                            )}
                          </div>
                          {canDeletePerm && (
                            <Button size="icon" variant="ghost" onClick={() => requestDeletePermission(p)} className="h-8 w-8 text-destructive hover:bg-destructive/10">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                            {[
                              { key: 'can_create', label: 'Create' },
                              { key: 'can_read', label: 'Read' },
                              { key: 'can_update', label: 'Update' },
                              { key: 'can_delete', label: 'Delete' },
                              { key: 'can_import', label: 'Import' },
                              { key: 'can_export', label: 'Export' },
                            ].map(({ key, label }) => {
                              if (p.resource === 'dashboard' && key !== 'can_read') return null;
                              const isReadPermission = key === 'can_read';
                              return (
                                <div key={key} className="flex items-center space-x-2">
                                  <Switch
                                    checked={p[key] ?? false}
                                    onCheckedChange={() => canUpdatePerm && dispatch(togglePermission({ permId: p.id, field: key }))}
                                    disabled={!canUpdatePerm}
                                    id={`${p.id}-${key}`}
                                    className={cn(isReadPermission && hasNoRead && "data-[state=checked]:bg-red-600")}
                                  />
                                  <Label htmlFor={`${p.id}-${key}`} className={cn("font-medium cursor-pointer", isReadPermission && hasNoRead && "text-red-700 font-semibold")}>
                                    {label}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                          {hasNoRead && (
                            <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-md text-red-800 text-xs">
                              <strong>Warning:</strong> Read permissions are mandatory to perform any actions on this resource.
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DIALOGS */}
      <AlertDialog open={isSaveConfirmOpen} onOpenChange={setIsSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Save with empty permissions?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>Some resources have no permissions enabled. Continue?</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performSave}>Yes, Save Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isAddPermConfirmOpen} onOpenChange={setIsAddPermConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Add Multiple Permissions?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>
            This will create {selectedResources.length} new permission{selectedResources.length > 1 ? 's' : ''} for the selected resources.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performBulkCreate}>Add All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isRoleDeleteOpen} onOpenChange={setIsRoleDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Role?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>You are about to delete <strong>{currentRole?.name}</strong>. This action cannot be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRole} className="bg-destructive">Delete Role</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isPermDeleteOpen} onOpenChange={setIsPermDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove Permission?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>Remove <strong>{permToDelete?.resource_display || permToDelete?.resource}</strong> from <strong>{currentRole?.name}</strong>?</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePermission} className="bg-destructive">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RolePage;